/* eslint-disable @typescript-eslint/no-explicit-any */
import {Fragment, useEffect} from "react";
import pxToRem from "hooks/usePxToRem";
import SidebarMenuData from "components/Sidebar/SidebarMenuData";

import {useDispatch, useSelector} from "react-redux";
import {Types as openNotifyTypes} from "store/reducers/drawerNotify";
import {Types as sidebarTypes} from "store/reducers/sidebarReducer";

import {Stack, Typography, List, Divider} from "@mui/material";

import SidebarNavItem from "../SidebarNavItem";

import StyledStack from "./SidebarNavigation.styles";
import {RetrieveUserInformation} from "services/User/user.service";
import {getListChat} from "services/Chat/chat.service";
import {Chat} from "components/Sidebar/Sidebar.types";
import useToast from "hooks/useToast";
import {Types as chatTypes} from "store/reducers/chatReducer";
import useLoading from "hooks/useLoading";
import {useLocation} from "react-router";
import {Creators as pendingEmailsCreators} from "store/reducers/pendingEmailsReducer";
import {GetListOfPendingEmails} from "services/api_v2/PendingEmails/PendingEmails.service";

interface SidebarNavigationProps {
  currentDropdownExpanded: string | null;
  onCurrentDropdownExpandedChange: (arg: string | null) => void;
}

const SidebarNavigation = ({
  currentDropdownExpanded,
  onCurrentDropdownExpandedChange,
}: SidebarNavigationProps) => {
  const {menuNavigationData} = SidebarMenuData();
  const dispatch = useDispatch();
  const stateChat = useSelector((state: any) => state.stateChat);
  const [showToast] = useToast();
  const [setLoading] = useLoading();
  const {
    userInfo: {
      settings: {expandedMenu},
    },
  } = useSelector((state: any) => state.stateUserInfo);

  const handleChangeCurrentDropdownExpanded = (dropdownTitle: string) => {
    if (dropdownTitle === currentDropdownExpanded)
      return onCurrentDropdownExpandedChange("");

    onCurrentDropdownExpandedChange(dropdownTitle);
  };

  const handleOpenDropdownMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    dispatch({
      type: sidebarTypes.CHANGE_ANCHOR_ELEMENT,
      currentAnchorElement: event.currentTarget,
    });
    if (currentDropdownExpanded !== "") return;

    onCurrentDropdownExpandedChange(event.currentTarget.ariaLabel);
  };

  const handleOpenNotificationDrawer = () => {
    dispatch({
      type: openNotifyTypes.OPEN_NOTIFY,
      open: true,
    });
  };

  const getNumberOfNotifications = (chats: Chat[]): number => {
    let numberOfNewChats = 0;

    chats.forEach((element) => {
      if (element.newMessages > 0) {
        numberOfNewChats++;
      }
    });

    return numberOfNewChats;
  };

  const getListOfChats = async () => {
    const user = RetrieveUserInformation();
    try {
      const response = await getListChat(user?._id);
      const newNotifications = getNumberOfNotifications(response?.data);

      if (newNotifications > 0 && newNotifications !== stateChat.newMessages) {
        showToast("Novo chat!", "warning");
      }
      dispatch({
        type: chatTypes.MY_LIST_CHAT,
        data: response.data,
        newMessages: newNotifications,
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (RetrieveUserInformation()) {
      const interval = setInterval(() => {
        getListOfChats();
      }, 300000);

      return () => clearInterval(interval);
    }
  }, []);

  const getPendingEmails = async () => {
    const response = await GetListOfPendingEmails({
      page: 1,
      limit: 1,
    });
    return response.data.meta.total;
  };

  const pendingEmailsCallback = async () => {
    try {
      const totalPendingEmails = await getPendingEmails();

      dispatch(pendingEmailsCreators.setPendingEmailsCount(totalPendingEmails));
    } catch (e) {
      console.error("Erro ao buscar pendências de e-mail (intervalo)", e);
    }
  };

  useEffect(() => {
    const oneHourInterval = setInterval(pendingEmailsCallback, 3600000); //1hora
    //processar no primeiro render
    pendingEmailsCallback();

    return () => clearInterval(oneHourInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const location = useLocation();

  useEffect(() => {
    if (RetrieveUserInformation()) {
      (async function () {
        if (!location.pathname.includes("chat")) {
          setLoading(true);
          await getListOfChats();
          setLoading(false);
        }
      })();
    }
  }, []);

  const formatNumberOfNewNotification = (numberOfNotifications: number) => {
    return numberOfNotifications > 0 ? numberOfNotifications : undefined;
  };

  const reorderedListByPosition = (itemToBeOrdered: any) => {
    return itemToBeOrdered.sort((a, b) => {
      if (a.position < b.position) {
        return -1;
      }
      if (a.position > b.position) {
        return 1;
      }
      return 0;
    });
  };

  const pendingCount = useSelector(
    (s: any) => s.statePendingEmails.pendingCount,
  );
  const badgeText = (name: string) => {
    if (name === "Chat") {
      return formatNumberOfNewNotification(stateChat?.newMessages);
    }
    if (name === "E-Mails Pendentes") {
      return formatNumberOfNewNotification(pendingCount);
    }

    return undefined;
  };

  return (
    <StyledStack spacing={pxToRem(20)} isSidebarOpen={expandedMenu}>
      {menuNavigationData?.map((groupItem: any, index: number, array: []) => {
        return (
          <Fragment key={groupItem.name + groupItem.id}>
            <Stack className="navigation" spacing={pxToRem(8)}>
              {expandedMenu && (
                <Typography
                  variant="subtitle2"
                  component="strong"
                  className="navigation__title"
                >
                  {groupItem.name}
                </Typography>
              )}

              <List className="navigation__list">
                {reorderedListByPosition(groupItem?.routes)?.map(
                  (navItem: any) => (
                    <SidebarNavItem
                      key={navItem.name}
                      name={navItem.name}
                      icon={navItem.icon}
                      {...(navItem.name === "Notificações"
                        ? {onClickRequest: handleOpenNotificationDrawer}
                        : {route: navItem.path})}
                      badgeText={badgeText(navItem.name)}
                    />
                  ),
                )}
                {reorderedListByPosition(groupItem.subCollection)?.map(
                  (subCollection: any) => (
                    <SidebarNavItem
                      key={subCollection.name}
                      name={subCollection.name}
                      icon={subCollection.icon}
                      onClickRequest={(event) =>
                        expandedMenu
                          ? handleChangeCurrentDropdownExpanded(
                              subCollection.name,
                            )
                          : handleOpenDropdownMenu(event)
                      }
                      dropdownContent={reorderedListByPosition(
                        subCollection.routes,
                      )}
                      currentDropdownExpanded={currentDropdownExpanded}
                      onExpandedDropdownChange={onCurrentDropdownExpandedChange}
                    />
                  ),
                )}
              </List>
            </Stack>

            {array.length - 1 !== index && <Divider />}
          </Fragment>
        );
      })}
    </StyledStack>
  );
};

export default SidebarNavigation;
